import { useGetCalendarEvents } from "@/shared/api/economic-calendar/useGetCalendarEvents";
import { CalendarEvent } from "@/shared/models/economic-calendar/CalendarEvent";
import * as Sentry from "@sentry/react";
import {
  countriesList,
  Country,
  getCountryDisplayLabel,
} from "@/shared/models/economic-calendar/countries";
import {
  ImportanceOption,
  importanceOptions,
} from "@/shared/models/economic-calendar/importanceOptions";
import { getFlagEmoji } from "@/utils/other";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStateWithOptionalPersistence } from "@/hooks/useStateWithOptionalPersistence";
import EconomicCalendarCountdown from "../EconomicCalendarCountdown";
import {
  formatLocalDate,
  formatTo24HourTime,
  formatToLongDate,
} from "@/utils/date-utils";
import clsx from "clsx";
import { BgOverlay } from "@/widget-components/firehose-widgets/firehose-all/components";

const getArrowAndColor = (act: string, exp: string) => {
  const actValue = parseFloat(act);
  const expValue = parseFloat(exp);

  if (!isNaN(actValue) && !isNaN(expValue)) {
    if (actValue > expValue) {
      return { color: "text-green-500", arrow: "▲" };
    } else if (actValue < expValue) {
      return { color: "text-red-500", arrow: "▼" };
    }
  }
  return { color: "text-gray-500 dark:text-gray-300", arrow: "" };
};

const formatValueWithUnit = (
  value: string | number | null | undefined,
  unit?: string | null
) => {
  if (!value && value !== 0) return null;
  if (!unit) return value;
  return `${value}${unit}`;
};

const isEventInPast = (eventDate: string): boolean => {
  const eventTime = new Date(eventDate).getTime();
  const currentTime = new Date().getTime();
  return eventTime < currentTime;
};

interface EconomicCalendarTableProps {
  headRowClassNames?: string;
  bodyRowClassNames?: string;

  headCellClassNames?: string;
  bodyCellClassNames?: string;

  /** Demo mode - shows previous day's data with disabled filters */
  isDemo?: boolean;

  /**
   * Per-instance identity when rendered as a dashboard widget. Two copies of
   * the calendar on one tab used to share the persisted country filter (and
   * even live-sync it across popout windows via storage events) because the
   * storage key was a static string.
   */
  widgetCompositeId?: string;
}

// Mobile Calendar View Component
interface MobileCalendarViewProps {
  events: CalendarEvent[] | undefined;
  highlightedEvent: CalendarEvent | null;
  rowRefs: React.MutableRefObject<Record<string, HTMLTableRowElement | null>>;
}

function MobileCalendarView({ events, highlightedEvent }: MobileCalendarViewProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (!events) return null;

  return (
    <div className="sm:hidden">
      {/* Sticky header row */}
      <div className="sticky top-0 z-10 flex items-center gap-2 px-2 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        <span className="w-10 shrink-0">Time</span>
        <span className="w-5 shrink-0"></span>
        <span className="flex-1">Event</span>
        <span className="shrink-0">Act / Exp</span>
        <span className="w-4 shrink-0"></span>
      </div>

      <div>
      {events.map((event, idx) => {
        const formattedTime = formatTo24HourTime(event.occurs_at);
        const isPast = isEventInPast(event.occurs_at);
        const isExpanded = expandedId === event.id;
        const isFirstUpcoming =
          idx > 0 && isEventInPast(events[idx - 1].occurs_at) && !isPast;

        const { color, arrow } = getArrowAndColor(
          event.actual ?? "",
          event.expected ?? ""
        );

        return (
          <React.Fragment key={event.id}>
            {/* Upcoming divider */}
            {isFirstUpcoming && (
              <div className="py-2 relative">
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-gradient-to-r from-squawk-blue/0 via-squawk-blue to-squawk-blue/0">
                  <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 px-2 text-squawk-blue text-[10px] font-semibold tracking-wide">
                    UPCOMING
                  </div>
                </div>
              </div>
            )}

            {/* Compact Row */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : event.id)}
              className={clsx(
                "w-full text-left px-2 py-2 transition-colors rounded-none border-t border-gray-200 dark:border-gray-700",
                isPast ? "bg-gray-50 dark:bg-gray-800/30" : "bg-white dark:bg-gray-900",
                highlightedEvent?.id === event.id && "bg-terminal-orange/20"
              )}
            >
              {/* Main row content */}
              <div className="flex items-center gap-2">
                {/* Time */}
                <span className="text-xs text-gray-500 dark:text-gray-400 w-10 shrink-0 font-mono">
                  {formattedTime}
                </span>

                {/* Flag */}
                {event.category?.location && (
                  <img
                    alt={event.category.location}
                    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${event.category.location}.svg`}
                    className="w-5 h-3.5 shrink-0 rounded-sm"
                  />
                )}

                {/* Event name */}
                <span className="flex-1 text-xs text-gray-900 dark:text-gray-100 truncate">
                  {event.title || event.category?.name || "Event"}
                </span>

                {/* ACT / EXP */}
                <div className="flex items-center gap-1 shrink-0">
                  <span className={clsx("text-xs font-bold", color)}>
                    {formatValueWithUnit(event.actual, event.category?.unit) || "—"}
                    {arrow && <span className="ml-0.5">{arrow}</span>}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500 text-xs">/</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatValueWithUnit(event.expected, event.category?.unit) || "—"}
                  </span>
                </div>

                {/* Expand indicator */}
                <svg
                  className={clsx(
                    "w-4 h-4 text-gray-400 transition-transform shrink-0",
                    isExpanded && "rotate-180"
                  )}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Expanded details */}
            {isExpanded && (
              <div className="px-2 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                {/* Full event name */}
                <p className="text-xs text-gray-900 dark:text-gray-100 font-medium mb-3">
                  {getCountryDisplayLabel(event.category?.location)} {event.title || event.category?.name}
                  {event.survey_date && (
                    <span className="text-gray-500 dark:text-gray-400 font-normal"> ({event.survey_date as string})</span>
                  )}
                </p>

                {/* Data grid */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Act</p>
                    <p className={clsx("text-xs font-bold", color)}>{formatValueWithUnit(event.actual, event.category?.unit) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Exp</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{formatValueWithUnit(event.expected, event.category?.unit) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Prev</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{formatValueWithUnit(event.previous, event.category?.unit) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Impact</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300 capitalize">{event.category?.importance || "—"}</p>
                  </div>
                </div>

                {/* Secondary data */}
                <div className="grid grid-cols-4 gap-2 text-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Low</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{formatValueWithUnit(event.low, event.category?.unit) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">High</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{formatValueWithUnit(event.high, event.category?.unit) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rev</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{formatValueWithUnit(event.revised, event.category?.unit) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">GMT</p>
                    <p className="text-xs text-gray-700 dark:text-gray-300">{formatTo24HourTime(event.occurs_at, "GMT")}</p>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
}

// Helper to get initial date (previous day for demo mode)
const getInitialDate = (isDemo: boolean): Date => {
  const date = new Date();
  if (isDemo) {
    date.setDate(date.getDate() - 1);
  }
  return date;
};

function EconomicCalendarTable(props: EconomicCalendarTableProps) {
  const [eventCalendarDate, setEventCalendarDate] = React.useState(() =>
    getInitialDate(props.isDemo || false)
  );

  const [highlightedEvent, setHighlightedEvent] =
    React.useState<CalendarEvent | null>(null);

  const [selectedImportance, setSelectedImportance] = React.useState<
    ImportanceOption[]
  >([]);

  const [selectedCountries, setSelectedCountries] =
    useStateWithOptionalPersistence<Country[]>(
      // Scoped to this instance on a dashboard; the standalone page passes no
      // composite id and keeps the legacy shared key.
      props.widgetCompositeId
        ? `economic-calendar.selectedCountries:${props.widgetCompositeId}`
        : "economic-calendar.selectedCountries",
      []
    );

  const { data } = useGetCalendarEvents(eventCalendarDate);

  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);

  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setStickyHeaderHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const filteredData = useMemo(() => {
    if (!data) return undefined;

    let result = data;

    if (selectedImportance.length > 0) {
      result = result.filter((event) =>
        selectedImportance.some(
          (option) =>
            option.value.toLocaleLowerCase() ===
            event.category?.importance?.toLocaleLowerCase()
        )
      );
    }

    if (selectedCountries.length > 0) {
      result = result.filter((event) =>
        selectedCountries.some(
          (country) => country.id === event.category?.location
        )
      );
    }

    return result;
  }, [data, selectedImportance, selectedCountries]);

  const scrollToRow = (id: number) => {
    const row = rowRefs.current[id];
    const container = containerRef.current;
    const stickyHeader = stickyHeaderRef.current;

    if (!row || !container) return;

    const headerHeight = stickyHeader?.offsetHeight ?? 0;

    // Find the actual scrollable element — in widget mode it's containerRef,
    // in full-screen mode the outer page wrapper scrolls instead.
    let scrollEl: HTMLElement = container;
    if (container.scrollHeight <= container.clientHeight) {
      let parent = container.parentElement;
      while (parent) {
        if (parent.scrollHeight > parent.clientHeight) {
          scrollEl = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    // Use getBoundingClientRect for reliable positioning regardless of nesting
    const rowRect = row.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const scrollTop =
      scrollEl.scrollTop + (rowRect.top - scrollRect.top) - headerHeight;

    scrollEl.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (highlightedEvent) {
      let timerId = setTimeout(() => setHighlightedEvent(null), 1500 * 2);

      return () => clearTimeout(timerId);
    }
  }, [highlightedEvent]);

  return (
    <div
      className="bg-white dark:bg-gray-900 p-2 pb-0 sm:pb-[23px] text-gray-900 dark:text-white flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-500 transition-colors"
      ref={containerRef}
    >
      {/* Sticky header: Filters + Countdown */}
      <div ref={stickyHeaderRef} className="sticky -top-2 z-[6] bg-white dark:bg-gray-900 -mx-2 px-2 pt-2 -mt-2 pb-1 border-b border-gray-200 dark:border-gray-700">
      <section className="flex gap-2 items-center pb-2">
        <div className={clsx("flex-1 min-w-20", props.isDemo && "opacity-50 pointer-events-none")}>
          <Listbox
            value={selectedCountries}
            onChange={setSelectedCountries}
            disabled={props.isDemo}
            multiple
          >
            <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white dark:bg-gray-800 py-0.5 pl-1 pr-1.5 text-left text-gray-900 dark:text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-squawk-blue sm:text-sm/6">
                <span className="col-start-1 row-start-1 truncate pr-6">
                  {selectedCountries
                    ?.map((country) => country.name)
                    .join(", ") || "Filter by Country"}
                </span>
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 dark:text-gray-400 sm:size-4"
                />
              </ListboxButton>
              <ListboxOptions
                anchor={{ to: "bottom start", gap: 4, padding: 8 }}
                transition
                className="z-10 max-h-60 w-[var(--button-width)] overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
              >
                {countriesList.map((country, idx) => (
                  <ListboxOption
                    key={country.id}
                    value={country}
                    className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 data-[focus]:bg-squawk-blue data-[focus]:text-white data-[focus]:outline-none"
                  >
                    <span className="block truncate font-normal group-data-[selected]:font-semibold ">
                      <span className="mr-2">{getFlagEmoji(country.id)}</span>
                      {country.name}
                    </span>

                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-squawk-blue group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                      <CheckIcon aria-hidden="true" className="size-5 " />
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
          </Listbox>
        </div>

        {selectedCountries.length > 0 && (
          <button
            onClick={() => setSelectedCountries([])}
            className="shrink-0 rounded-md bg-white dark:bg-gray-800 py-0.5 px-2 text-xs text-gray-500 dark:text-gray-400 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 sm:text-sm/6"
          >
            Clear
          </button>
        )}

        <input
          type="date"
          name="eventCalendarDate"
          disabled={props.isDemo}
          autoComplete="off"
          className={clsx(
            "text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded py-0.5 px-1.5 flex-1",
            props.isDemo && "opacity-50 cursor-not-allowed"
          )}
          defaultValue={formatLocalDate(eventCalendarDate)}
          onChange={(e) => {
            if (!props.isDemo) {
              setEventCalendarDate(new Date(e.target.value + "T00:00:00"));
            }
          }}
        />

        <div className={clsx("flex-1 min-w-20", props.isDemo && "opacity-50 pointer-events-none")}>
          <Listbox
            value={selectedImportance}
            onChange={setSelectedImportance}
            disabled={props.isDemo}
            multiple
          >
            <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white dark:bg-gray-800 py-0.5 pl-1 pr-1.5 text-left text-gray-900 dark:text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-squawk-blue sm:text-sm/6">
                <span className="col-start-1 row-start-1 truncate pr-6">
                  {selectedImportance
                    ?.map((option) => option.value)
                    .join(", ") || "Select Importance"}
                </span>
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 dark:text-gray-400 sm:size-4"
                />
              </ListboxButton>
              <ListboxOptions
                anchor={{ to: "bottom start", gap: 4, padding: 8 }}
                transition
                className="z-10 max-h-60 w-[var(--button-width)] overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
              >
                {importanceOptions.map((option, idx) => (
                  <ListboxOption
                    key={option.id}
                    value={option}
                    className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 data-[focus]:bg-squawk-blue data-[focus]:text-white data-[focus]:outline-none"
                  >
                    <span className="block truncate font-normal group-data-[selected]:font-semibold">
                      {option.value}
                    </span>

                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-squawk-blue group-[&:not([data-selected])]:hidden group-data-[focus]:text-white">
                      <CheckIcon aria-hidden="true" className="size-5 " />
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
          </Listbox>
        </div>
      </section>

      {!props.isDemo && (
        <Sentry.ErrorBoundary
          fallback={<p className="text-red-500 text-xs">Calendar error</p>}
        >
          <EconomicCalendarCountdown
            onClick={(e) => {
              scrollToRow(e.id);
              setHighlightedEvent({ ...e });
            }}
          />
        </Sentry.ErrorBoundary>
      )}
      </div>

      {/* Desktop Table - hidden on mobile */}
      <table className="hidden sm:table min-w-full divide-y divide-gray-300 dark:divide-gray-400 overflow-x-scroll">
        <thead className="bg-gray-100 dark:bg-gray-800 sticky py-2 z-[5]" style={{ top: stickyHeaderHeight }}>
          <tr className={clsx(props.headRowClassNames)}>
            <th className="px-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 ">
              TIME
            </th>
            <th className="px-1 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center min-w-20">
              TIME GMT
            </th>
            <th className="px-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              COUNTRY
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              EVENT
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300 bg-squawk-blue/10">
              ACT
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              EXP
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              LOW
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              HIGH
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              PREV
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              REV FROM
            </th>
            <th className="px-1  text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
              IMPACT
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
          {filteredData?.length === 0 && (
            <tr>
              <td colSpan={11} className="text-center text-gray-400 dark:text-gray-500 text-xs py-8">
                No calendar events match the selected filters.
              </td>
            </tr>
          )}
          {filteredData?.map((event, idx) => {
            const formatDateString = formatToLongDate(event.occurs_at);
            const formattedTimeString = formatTo24HourTime(event.occurs_at);
            const gmtTimeString = formatTo24HourTime(event.occurs_at, "GMT");

            const { color, arrow } = getArrowAndColor(
              event.actual ?? "",
              event.expected ?? ""
            );

            const isPast = isEventInPast(event.occurs_at);
            const isFirstUpcoming =
              idx > 0 &&
              isEventInPast(filteredData[idx - 1].occurs_at) &&
              !isPast;

            return (
              <React.Fragment key={event.id}>
                {isFirstUpcoming && (
                  <tr>
                    <td colSpan={11} className="py-2 relative">
                      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[1px] bg-gradient-to-r from-squawk-blue/0 via-squawk-blue via-50% to-squawk-blue/0">
                        <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 px-2 text-squawk-blue text-[10px] font-semibold tracking-wide">
                          UPCOMING
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                <tr
                  ref={(el) => (rowRefs.current[event.id] = el)}
                  className={clsx(
                    "relative",
                    isPast ? "bg-white dark:bg-gray-800/30" : "bg-white dark:bg-gray-900",
                    props.bodyRowClassNames
                  )}
                >
                  <td
                    className={clsx(
                      "whitespace-nowrap px-1 ",
                      props.bodyCellClassNames
                    )}
                  >
                    {formattedTimeString}
                  </td>

                  <td className="whitespace-nowrap px-1   text-center">
                    {gmtTimeString}
                  </td>

                  <td className="whitespace-nowrap px-1  text-center">
                    <span className="flex justify-center">
                      {event.category?.location && (
                        <img
                          alt={event.category?.location}
                          src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${event.category.location}.svg`}
                          className="size-4"
                        />
                      )}
                    </span>
                  </td>
                  <td className="px-1  whitespace-normal leading-normal min-w-[150px]">
                    <p>
                      <span className="text-gray-500 dark:text-gray-400">{getCountryDisplayLabel(event.category?.location)} </span>
                      {event.title ||
                        event.category?.name ||
                        "Untitled Event"}{" "}
                      ({event.survey_date || "N/A"})
                    </p>
                  </td>
                  <td className={`whitespace-nowrap px-1 text-xs font-extrabold bg-squawk-blue/5 ${color}`}>
                    {formatValueWithUnit(event.actual, event.category?.unit)} {arrow}
                  </td>
                  <td className="whitespace-nowrap px-1 ">{formatValueWithUnit(event.expected, event.category?.unit)}</td>
                  <td className="whitespace-nowrap px-1 ">{formatValueWithUnit(event.low, event.category?.unit)}</td>
                  <td className="whitespace-nowrap px-1 ">{formatValueWithUnit(event.high, event.category?.unit)}</td>
                  <td className="whitespace-nowrap px-1 ">{formatValueWithUnit(event.previous, event.category?.unit)}</td>
                  <td className="whitespace-nowrap px-1 ">{formatValueWithUnit(event.revised, event.category?.unit)}</td>
                  <td className="whitespace-nowrap px-1 ">
                    {event.category?.importance}
                  </td>

                  {highlightedEvent?.id === event.id && (
                    <BgOverlay
                      duration={3000}
                      startTime={new Date()}
                      delay={0}
                      color="#FF6600"
                    />
                  )}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Mobile View - Condensed expandable rows */}
      <MobileCalendarView
        events={filteredData}
        highlightedEvent={highlightedEvent}
        rowRefs={rowRefs}
      />

    </div>
  );
}

export default EconomicCalendarTable;
